<template>
    <BaseView contentClass="bg-white" :loader="false">
        <template v-slot:top>
            <TitleBar
                :title="$t('Profile')"
                @left-click="close"
                @right-click="submit"
            >
                <template v-slot:leftIcon>
                    <CloseIcon />
                </template>
                <template v-slot:rightIcon>
                    <SaveIcon />
                </template>
            </TitleBar>
        </template>

        <input-control :label="$t('Name')">
          <input
            id="name"
            v-model.trim.lazy="$v.name.$model"
            name="name"
            type="text"
            :placeholder="$t('Name')"
            :class="inputClass($v.name.$anyError)"
          />
          <div v-if="$v.name.$dirty">
            <input-error :condition="!$v.name.required">
              {{ $t('The {field} field is required', { field: $t('name') }) }}
            </input-error>
          </div>
        </input-control>

        <input-control :label="$t('E-mail address')">
          <input
            id="email"
            v-model.trim.lazy="$v.email.$model"
            type="text"
            name="email"
            :placeholder="$t('E-mail address')"
            :class="inputClass($v.email.$anyError)"
          />
          <div v-if="$v.email.$dirty">
            <input-error :condition="!$v.email.required">
              {{ $t('The {field} field is required', { field: $t('e-mail address') }) }}
            </input-error>
            <input-error :condition="!$v.email.email">
              {{ $t('The e-mail address is not valid') }}
            </input-error>
          </div>
        </input-control>

        <input-control-toggle :label="$t('Send push notifications (Android only)')">
          <toggle-button
            v-model="notify_by_push"
            :color="{
                checked: 'var(--primary)',
                unchecked: 'var(--50)',
                disabled: 'var(--20)',
            }"
          />
        </input-control-toggle>

        <input-control-toggle :label="$t('Send notifications by e-mail')" :starred="isOnFreePlan">
          <toggle-button
            v-model="notify_by_email"
            :color="{
                checked: 'var(--primary)',
                unchecked: 'var(--50)',
                disabled: 'var(--20)',
            }"
            @click.native="showFreePlanModal"
            :disabled="isOnFreePlan"
          />
        </input-control-toggle>
        <input-control v-if="notify_by_email" :label="$t('Notification e-mail address')">
          <input
            id="notification_email"
            v-model.trim.lazy="$v.notification_email.$model"
            type="text"
            name="notification_email"
            :placeholder="$t('e-mail address')"
            :class="inputClass($v.notification_email.$anyError)"
          />
          <div v-if="$v.notification_email.$dirty">
            <input-error :condition="!$v.notification_email.required">
              {{ $t('The {field} field is required', { field: $t('notification e-mail address') }) }}
            </input-error>
            <input-error :condition="!$v.notification_email.email">
              {{ $t('The notification e-mail address is not valid') }}
            </input-error>
          </div>
        </input-control>

        <input-control-toggle :label="$t('Send notifications by SMS')" :starred="isOnFreePlan">
          <toggle-button
            v-model="notify_by_sms"
            :color="{
                checked: 'var(--primary)',
                unchecked: 'var(--50)',
                disabled: 'var(--20)',
            }"
            @click.native="showFreePlanModal"
            :disabled="isOnFreePlan"
          />
        </input-control-toggle>
        <input-control v-if="notify_by_sms" :label="$t('Phone number')">
          <input
            id="phone_number"
            v-model.trim.lazy="phone_number"
            type="text"
            name="phone_number"
            :placeholder="$t('Phone number')"
            :class="inputClass()"
          />
          <div v-if="$v.phone_number.$dirty">
            <input-error :condition="!$v.phone_number.required">
              {{ $t('The {field} field is required', { field: $t('phone number') }) }}
            </input-error>
          </div>
        </input-control>

        <input-control :label="$t('Password')">
          <input
            id="password"
            v-model.trim.lazy="$v.password.$model"
            type="password"
            name="password"
            placeholder="*******"
            :class="inputClass($v.password.$anyError)"
          />
          <div v-if="$v.password.$dirty">
            <input-error :condition="!$v.password.minLength">{{ $t('The password must be at least 6 characters long.') }}</input-error>
          </div>
        </input-control>

        <input-control :label="$t('Password Confirmation')">
          <input
            id="password_confirmation"
            v-model.trim.lazy="$v.password_confirmation.$model"
            type="password"
            name="password_confirmation"
            placeholder="*******"
            autocomplete="new-password"
            :class="inputClass($v.password_confirmation.$anyError)"
          />
          <div v-if="$v.password_confirmation.$dirty">
            <input-error :condition="!$v.password_confirmation.sameAsPassword">{{ $t('The password confirmation does not match the password.') }}</input-error>
          </div>
        </input-control>

        <input-control :label="$t('Language')">
          <select
            id="locale"
            v-model.trim.lazy="$v.locale.$model"
            class="w-full form-input form-control form-select"
          >
            <option
              v-for="(locale, index) in locales"
              :key="index"
              :value="locale.short"
              :selected="locale.short === user.locale"
            >{{ locale.full }}</option>
          </select>
        </input-control>

        <input-control-toggle :label="$t('On Duty')">
          <toggle-button v-model="on_duty" :color="{
            checked: 'var(--primary)',
            unchecked: 'var(--50)',
            disabled: 'var(--20)',
        }" @input="on_duty = !on_duty" />
        </input-control-toggle>

        <div class="mb-6">
          <div class="mb-2">
            <h4 class="font-normal text-90">{{ $t('Role') }}</h4>
          </div>
          <div>
            <div class="text-80">{{user.roles.join(", ")}}</div>
          </div>
        </div>

      <button class="btn btn-default btn-primary" @click.prevent="submit">{{ $t('Save Profile') }}</button>

  </BaseView>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
    required, email, minLength, sameAs, requiredIf,
} from 'vuelidate/lib/validators';
import BaseView from '@mobile/components/BaseView.vue';
import SaveIcon from '@mobile/components/Icons/SaveIcon.vue';
import CloseIcon from '@mobile/components/Icons/CloseIcon.vue';
import OnFreePlan from '@mobile/mixins/OnFreePlan';
import TitleBar from '@/components/TitleBar.vue';
import InputControl from '../components/Form/InputControl.vue';
import InputError from '../components/Form/InputError.vue';
import InputControlToggle from '../components/Form/InputControlToggle.vue';
import { eventBus } from '@/eventBus';

export default {
    name: 'Profile',

    mixins: [
        OnFreePlan,
    ],

    components: {
        'input-control': InputControl,
        'input-error': InputError,
        'input-control-toggle': InputControlToggle,
        BaseView,
        TitleBar,
        SaveIcon,
        CloseIcon,
    },

    data() {
        return {
            password_confirmation: '',
            password: '',
            name: '',
            email: '',
            notification_email: '',
            locale: '',
            notify_by_push: false,
            notify_by_email: false,
            phone_number: '',
            notify_by_sms: false,
            on_duty: false,

            locales: [
                { short: 'en', full: this.$t('English') },
                { short: 'de', full: this.$t('German') },
                { short: 'nl', full: this.$t('Dutch') },
                { short: 'fr', full: this.$t('French') },
                { short: 'es', full: this.$t('Spanish') },
                { short: 'pt-BR', full: this.$t('Portuguese (Brazilian)') },
            ],

            canGoBack: false,
        };
    },

    created() {
        this.name = this.user.name;
        this.email = this.user.email;
        this.locale = this.user.locale;
        this.notify_by_push = Boolean(this.user.notify_by_push);
        this.notify_by_email = Boolean(this.user.notify_by_email);
        this.notification_email = this.user.notification_email;
        this.notify_by_sms = Boolean(this.user.notify_by_sms);
        this.phone_number = this.user.phone_number;
        this.on_duty = Boolean(this.user.on_duty);
    },

    validations: {
        name: { required },
        email: { required, email },
        notification_email: {
            required: requiredIf((form) => form.notify_by_email),
            email,
        },
        phone_number: {
            required: requiredIf((form) => form.notify_by_sms),
        },
        locale: { required },
        password: { minLength: minLength(6) },
        password_confirmation: { sameAsPassword: sameAs('password') },
    },

    // this makes sure we only go back on close if that is still inside the app
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            if (from.matched.length > 0) {
                vm.canGoBack = true;
            }
        });
    },

    methods: {
        ...mapActions({
            submitForm: 'profile/submit',
        }),

        showFreePlanModal() {
            if (this.isOnFreePlan) {
                eventBus.$emit('free-plan-limit', {
                    message: {
                        advice: this.$t('Upgrade on desktop and receive also E-mail and SMS notifications when inspections or action gets available and inspections of colleagues become overdue.'),
                    },
                    shouldRedirect: false,
                });
            }
        },

        async submit() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.submitForm({
                password_confirmation: this.password_confirmation,
                password: this.password,
                name: this.name,
                email: this.email,
                locale: this.locale,
                notify_by_push: this.notify_by_push,
                notification_email: this.notification_email,
                notify_by_email: this.notify_by_email,
                phone_number: this.phone_number,
                notify_by_sms: this.notify_by_sms,
                on_duty: this.on_duty,

                toast: (message, type = 'success') => {
                    this.$toasted.show(this.$t(message), { type });
                },
            });
        },

        inputClass(error = false) {
            return {
                'w-full': true,
                'form-control': true,
                'form-input': true,
                'form-input-bordered': true,
                'border-red': error,
            };
        },

        close() {
            if (this.canGoBack) {
                this.$router.go(-1);
            } else {
                this.$router.push({ name: 'todos' });
            }
        },
    },
};
</script>

<style>
.margins {
  margin-top: 10px;
  margin-bottom: 10px;
}
</style>
